// Domain status

import { DEFAULT_DOMAIN_SUFFIX } from '../../../globals';

//
export enum DomainStatus {
  pending = 'pending',
  connected = 'connected',
  disconnected = 'disconnected',
  deleting = 'deleting',
}

export interface DomainsResponseJSON {
  data: DomainsJSON;
}

// Domain JSON
export interface DomainJSON {
  domain: string;
  status: string;
  is_primary: boolean;
  has_ssl_cert: boolean;
  is_aaaa_detected: boolean;
  can_ping: boolean;
  created: Date;
  updated: Date;
}

// Domains JSON
interface DomainsJSON {
  domains: DomainJSON[];
}

// Domain
export class Domain {
  public translateStatusKey: string;
  public translateSSLStatusKey: string;

  public constructor(
    public domain: string,
    public status: string,
    public isPrimary: boolean,
    public hasSSLCert: boolean,
    public canPing: boolean,
    public created: Date,
    public updated: Date,
    public isAAAADetected: boolean,
  ) {
    switch (this.status) {
      case DomainStatus.pending: {
        this.translateStatusKey = 'PAGES.DOMAINS.STATUS.PENDING';
        break;
      }
      case DomainStatus.connected: {
        this.translateStatusKey = 'PAGES.DOMAINS.STATUS.CONNECTED';
        break;
      }
      case DomainStatus.disconnected: {
        this.translateStatusKey = 'PAGES.DOMAINS.STATUS.DISCONNECTED';
        break;
      }
      case DomainStatus.deleting: {
        this.translateStatusKey = 'PAGES.DOMAINS.STATUS.DELETING';
        break;
      }
    }
    if (this.hasSSLCert) {
      this.translateSSLStatusKey = 'PAGES.DOMAINS.SSL_STATUS.SECURE';
    } else {
      this.translateSSLStatusKey = 'PAGES.DOMAINS.SSL_STATUS.PENDING';
    }
  }

  // Returns true if this is a default domain.
  get isDefault(): boolean {
    return this.domain.endsWith(DEFAULT_DOMAIN_SUFFIX);
  }

  // Returns true if the domain should be reconnected.
  get shouldReconnect(): boolean {
    return this.status === DomainStatus.pending || this.status === DomainStatus.disconnected;
  }

  // Returns true if the domain can be set to primary.
  get canSetPrimary(): boolean {
    return !this.isPrimary && this.status === DomainStatus.connected;
  }

  // Returns true if the domain can be deleted.
  get canDelete(): boolean {
    return !this.isPrimary && !this.isDefault && this.status !== DomainStatus.deleting;
  }

  // Returns true if any action is allowed on the domain.
  get actionsAllowed(): boolean {
    return this.canSetPrimary || this.canDelete;
  }

  // Constructs an object from a JSON-encoded domain
  public static fromJSON(domainJSON: DomainJSON): Domain {
    let status = domainJSON.status;
    if (domainJSON.status === DomainStatus.connected && !domainJSON.can_ping) {
      status = DomainStatus.disconnected.toString();
    }
    return new Domain(
      domainJSON.domain,
      status,
      domainJSON.is_primary,
      domainJSON.has_ssl_cert,
      domainJSON.can_ping,
      new Date(domainJSON.created),
      new Date(domainJSON.updated),
      domainJSON.is_aaaa_detected,
    );
  }
}

// Domains
export class Domains {
  public constructor(public domainsList: Domain[]) {}

  // Constructs an object from JSON-encoded domains
  public static fromJSON(domainsJSON: DomainsJSON): Domains {
    const domains: Domain[] = [];
    for (const domain of domainsJSON.domains) {
      domains.push(Domain.fromJSON(domain));
    }
    return new Domains(domains);
  }
}
